import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Constants } from '@pwc/pwc-deals-web-common';
import { getLinkedAdjustmentsAsSourceForNwc } from '../../../../../common/engine/selectors/adjustmentLinkSelectors';
import {
  getAdjustmentTypesWorkingCapitalContributionForNwc,
  getTableReadyAdjustmentSumsParticipatingInCalculationsForNwc,
  getTableReadyAdjustmentSumsNotParticipatingInCalculationsForNwc,
} from '../../../../../common/engine/selectors/adjustmentSelectors';
import { getAdjustmentTogglesForNwc } from '../../../../../common/engine/selectors/adjustmentTogglesSelectors';
import FinalBorderRow from '../../common/FinalBorderRow';
import OverallTableHeader from '../../common/OverallTableHeader';
import { getActiveTableTimePeriodsForNwc } from '../../../../../common/engine/selectors/timePeriodSelectors';
import {
  CURRENT_ASSETS_KEY,
  CURRENT_LIABILITIES_KEY,
  OTHER_CONSIDERATIONS_TOTALS_OVERRIDE_TEXT,
} from '../../../../../common/engine/utilities/constants';
import ReportedFinancials from '../../common/ReportedFinancials';
import {
  getCurrentAssets,
  getCurrentLiabilities,
  getReportedWorkingCapital,
} from '../../../../../common/engine/selectors/factCalculationsSelectors';
import AdjustmentGroupForMode from '../../common/AdjustmentGroup/AdjustmentGroupForMode';

const NetWorkingCapitalTable = ({
  isClientView,
  contentVersionNumber,
  contentId,
  projectId,
  forUseWithPublishingSSRService,
  nameOfAdjustmentToExpand,
  targetAdjustmentType,
  targetAdjustmentName,
  isEditMode,
  adjustmentOptions,
  targetDocumentOptions,
  selectedLinkedAdjustments,
  setFieldValue,
  showLinkedAdjustments,
}) => {
  const nwcTableViewTimePeriods = useSelector(getActiveTableTimePeriodsForNwc);
  const participatingAdjustmentSums = useSelector(
    getTableReadyAdjustmentSumsParticipatingInCalculationsForNwc
  );
  const notParticipatingAdjustmentSums = useSelector(
    getTableReadyAdjustmentSumsNotParticipatingInCalculationsForNwc
  );
  const adjustmentTypesWorkingCapitalContribution = useSelector(
    getAdjustmentTypesWorkingCapitalContributionForNwc
  );
  const currentAssets = useSelector(getCurrentAssets);
  const currentLiabilities = useSelector(getCurrentLiabilities);
  const reportedWorkingCapital = useSelector(getReportedWorkingCapital);
  const nwcAdjustmentToggles = useSelector(getAdjustmentTogglesForNwc);
  const links = useSelector(getLinkedAdjustmentsAsSourceForNwc);
  const linkedAdjustments = links.filter(
    (link) => !isClientView || !link.isBroken
  );
  const isPublishMode = forUseWithPublishingSSRService;

  return (
    <table
      role="table"
      className="analysis-table"
      data-testid="structured-analysis-view-NWC"
      aria-label="Net Working Capital"
    >
      <thead>
        <OverallTableHeader
          timePeriods={nwcTableViewTimePeriods}
          documentCode={Constants.NET_WORKING_CAPITAL}
          isPublishMode={isPublishMode}
        />
      </thead>
      <tbody>
        <ReportedFinancials
          testId="current-assets"
          rowText={CURRENT_ASSETS_KEY}
          financials={currentAssets}
          timePeriods={nwcTableViewTimePeriods}
          documentCode={Constants.NET_WORKING_CAPITAL}
          isPublishMode={isPublishMode}
        />
        <ReportedFinancials
          testId="current-liabilities"
          rowText={CURRENT_LIABILITIES_KEY}
          financials={currentLiabilities}
          timePeriods={nwcTableViewTimePeriods}
          documentCode={Constants.NET_WORKING_CAPITAL}
          isPublishMode={isPublishMode}
        />
        <ReportedFinancials
          testId="reported-working-capital"
          rowText="Reported Working Capital"
          financials={reportedWorkingCapital}
          timePeriods={nwcTableViewTimePeriods}
          className="reported-working-capital"
          documentCode={Constants.NET_WORKING_CAPITAL}
          isPublishMode={isPublishMode}
        />

        {participatingAdjustmentSums.map((adjustmentTypeGroup) => (
          <AdjustmentGroupForMode
            projectId={projectId}
            documentCode={Constants.NET_WORKING_CAPITAL}
            contentId={contentId}
            contentVersionNumber={contentVersionNumber}
            key={adjustmentTypeGroup.adjustmentType}
            group={adjustmentTypeGroup.adjustments}
            groupName={adjustmentTypeGroup.adjustmentType}
            timePeriods={nwcTableViewTimePeriods}
            workingCapitalContributions={
              adjustmentTypesWorkingCapitalContribution
            }
            linkedAdjustments={linkedAdjustments}
            adjustmentToggles={nwcAdjustmentToggles}
            isLastGroup={adjustmentTypeGroup.isLastGroup}
            targetAdjustmentType={targetAdjustmentType}
            targetAdjustmentName={targetAdjustmentName}
            nameOfAdjustmentToExpand={nameOfAdjustmentToExpand}
            isPublishMode={isPublishMode}
            isEditMode={isEditMode}
            adjustmentOptions={adjustmentOptions}
            targetDocumentOptions={targetDocumentOptions}
            selectedLinkedAdjustments={selectedLinkedAdjustments}
            setFieldValue={setFieldValue}
            showLinkedAdjustments={showLinkedAdjustments}
            isClientView={isClientView}
          />
        ))}
        {notParticipatingAdjustmentSums.map((adjustmentTypeGroup) => (
          <AdjustmentGroupForMode
            projectId={projectId}
            documentCode={Constants.NET_WORKING_CAPITAL}
            contentId={contentId}
            contentVersionNumber={contentVersionNumber}
            key={adjustmentTypeGroup.adjustmentType}
            group={adjustmentTypeGroup.adjustments}
            groupName={adjustmentTypeGroup.adjustmentType}
            timePeriods={nwcTableViewTimePeriods}
            linkedAdjustments={linkedAdjustments}
            totalOverrideValue={OTHER_CONSIDERATIONS_TOTALS_OVERRIDE_TEXT}
            showToggles={false}
            showTotalRow={false}
            targetAdjustmentType={targetAdjustmentType}
            targetAdjustmentName={targetAdjustmentName}
            nameOfAdjustmentToExpand={nameOfAdjustmentToExpand}
            isPublishMode={isPublishMode}
            isEditMode={isEditMode}
            adjustmentOptions={adjustmentOptions}
            targetDocumentOptions={targetDocumentOptions}
            selectedLinkedAdjustments={selectedLinkedAdjustments}
            setFieldValue={setFieldValue}
            showLinkedAdjustments={showLinkedAdjustments}
            isClientView={isClientView}
          />
        ))}
        <FinalBorderRow
          timePeriods={nwcTableViewTimePeriods}
          documentCode={Constants.NET_WORKING_CAPITAL}
          isPublishMode={isPublishMode}
        />
      </tbody>
    </table>
  );
};

NetWorkingCapitalTable.propTypes = {
  isClientView: PropTypes.bool,
  contentId: PropTypes.number.isRequired,
  contentVersionNumber: PropTypes.number.isRequired,
  projectId: PropTypes.string.isRequired,
  nameOfAdjustmentToExpand: PropTypes.string,
  targetAdjustmentType: PropTypes.string,
  targetAdjustmentName: PropTypes.string,
  forUseWithPublishingSSRService: PropTypes.bool,
};

NetWorkingCapitalTable.defaultProps = {
  isClientView: false,
  targetAdjustmentType: null,
  targetAdjustmentName: null,
  nameOfAdjustmentToExpand: null,
  forUseWithPublishingSSRService: false,
};

export default NetWorkingCapitalTable;
